<script setup>
import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom'
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'

// Component name: TooltipText (multi-word compliance)
defineOptions({
    name: 'TooltipText',
})

const props = defineProps({
    content: String,
    placement: {
        type: String,
        default: 'bottom',
    },
})

const tooltipId = useId()
const ariaAttrs = { 'aria-describedby': tooltipId }

const referenceRef = ref()
const floatingRef = ref()
const arrowRef = ref()
const isHidden = ref(true)

// Bound to the consumer's real trigger element via `:ref="setReference"`,
// the same pattern DropdownMenu.vue uses, so we never render a wrapper
// element that would nest inside (or around) the consumer's own
// interactive content. Router-link and other component refs expose their
// root DOM node via `$el` rather than being a DOM node themselves.
function setReference(el) {
    referenceRef.value = el?.$el ?? el
}

async function calculatePosition() {
    const { x, y, middlewareData, placement } = await computePosition(
        referenceRef.value,
        floatingRef.value,
        {
            placement: props.placement,
            middleware: [
                offset(8),
                flip(),
                shift({ padding: 5 }),
                arrow({ element: arrowRef.value }),
            ],
        }
    )

    // The component may have unmounted (or hide() may have run) while this
    // was in flight, clearing the refs — bail out rather than crash.
    if (!floatingRef.value || !arrowRef.value) {
        return
    }

    Object.assign(floatingRef.value.style, {
        left: `${x}px`,
        top: `${y}px`,
    })

    const { x: arrowX, y: arrowY } = middlewareData.arrow || {}

    const opposedSide = {
        left: 'right',
        right: 'left',
        bottom: 'top',
        top: 'bottom',
    }[placement.split('-')[0]]

    Object.assign(arrowRef.value.style, {
        left: arrowX ? `${arrowX}px` : '',
        top: arrowY ? `${arrowY}px` : '',
        bottom: '',
        right: '',
        [opposedSide]: '-4px',
    })
}

function hide() {
    isHidden.value = true
}

async function show() {
    isHidden.value = false
    await nextTick()
    await calculatePosition()
}

function handleKeydown(event) {
    if (event.key === 'Escape') {
        hide()
    }
}

// Listens globally rather than on the trigger itself so Escape dismisses
// the tooltip whether it was opened by hover or by focus (WCAG 1.4.13).
watch(isHidden, (hidden) => {
    if (hidden) {
        window.removeEventListener('keydown', handleKeydown)
    } else {
        window.addEventListener('keydown', handleKeydown)
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
    <slot
        :set-reference="setReference"
        :aria-attrs="ariaAttrs"
        :show="show"
        :hide="hide"
    />
    <div
        :id="tooltipId"
        ref="floatingRef"
        role="tooltip"
        :class="[
            'absolute top-0 left-0 z-50 max-w-xs font-sans normal-case bg-taupe-500 text-sm text-white px-3 py-1.5 rounded-md cursor-default',
            isHidden && 'hidden',
        ]"
    >
        {{ props.content }}
        <div
            class="absolute bg-taupe-500 h-[8px] w-[8px] rotate-45"
            ref="arrowRef"
        ></div>
    </div>
</template>
